/**
 * Shared "list of AI agents for this user+location" query hooks.
 *
 * GET /assistant/fetchbyuser?user_id=...&location_id=... is the per-user
 * agent list. It used to be fetched independently via raw axios.get in:
 *   - context/usersContext.js (fetchAgentsArray, feeding the YourAIAgents
 *     grid + SingleWebpage.jsx's knowledge-base agent dropdown)
 *   - pages/Settings/components/ApiAccess/index.js (getSelection + Assistant)
 *   - pages/Settings/components/integration/index.js (getSelection + Assistant)
 *   - components/addwebsites/index.js (getSelection)
 *   - components/uploadFiles/index.js (getSelection)
 * Every call site does its own local filtering/reshaping of the SAME raw
 * response body, so useAgentsList returns that body (`{ data: [...agents],
 * crm_agent, ... }`) unfiltered/unshaped — filtering stays at each call site,
 * exactly as before.
 *
 * GET /assistant/getAccountlevelassistants?type=account&assist_type=account-only&location_id=...
 * is a DIFFERENT backend route (account-scoped, no user_id) used only by
 * Settings > AgentManager (the legacy admin agent table), previously called
 * through the service registry (AppService.assistant.getAccountlevelassistants).
 * Kept as its own hook, useAccountLevelAgents, rather than force-merged with
 * useAgentsList — same "agents" concept, different endpoint/shape.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const agentsKeys = {
  all: ["agents"],
  list: (userId, locationId) => [...agentsKeys.all, "list", userId, locationId],
};

export function fetchAgentsByUser(userId, locationId) {
  return request(GET, "/assistant/fetchbyuser", {
    query: `user_id=${userId}&location_id=${locationId}`,
  });
}

export function useAgentsList(userId, locationId) {
  return useQuery({
    queryKey: agentsKeys.list(userId, locationId),
    queryFn: () => fetchAgentsByUser(userId, locationId),
    enabled: !!userId && !!locationId,
  });
}

export const accountLevelAgentsKeys = {
  all: ["accountLevelAgents"],
  list: (locationId) => [...accountLevelAgentsKeys.all, "list", locationId],
};

export function useAccountLevelAgents(locationId) {
  return useQuery({
    queryKey: accountLevelAgentsKeys.list(locationId),
    queryFn: async () => {
      const res = await request(GET, "/assistant/getAccountlevelassistants", {
        query: `type=account&assist_type=account-only&location_id=${locationId}`,
      });
      return res?.data || [];
    },
    enabled: !!locationId,
  });
}

/**
 * Write side for /assistant (create/update/delete). Both the per-user agent
 * list (YourAIAgents grid, agentsKeys.list(userId, locationId)) and the
 * account-level agent table (Settings > AgentManager,
 * accountLevelAgentsKeys.list(locationId)) create/update through this SAME
 * POST/PUT /assistant endpoint — only which cache entry needs invalidating
 * differs by call site. So these mutations don't invalidate anything
 * themselves; each call site passes its own onSuccess (or awaits
 * mutateAsync and does it inline) and invalidates the query key(s) it
 * actually reads, exactly like the pre-existing handleRefresh callbacks
 * already wired to accountLevelAgentsKeys in Settings/AgentManager/index.js.
 */
export function useCreateAgent() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant", { payload }),
  });
}

export function useUpdateAgent() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/assistant", { payload }),
  });
}

export function useDeleteAgent() {
  return useMutation({
    mutationFn: ({ _id, assistant_id }) =>
      request(DELETE, "/assistant", {
        query: `_id=${_id}&assistant_id=${assistant_id}`,
      }),
  });
}

// Settings > AgentManager's two "default agent" toggles — previously raw
// axios.post to ${env.API_URL}/v1/assistant/... (bypassing both the
// useAppServices registry AND apiClient). Ported here so they share the
// baseURL/error-throwing behavior every other query/mutation hook gets.
export function useUpdateDefaultAssistant() {
  return useMutation({
    mutationFn: ({ assistantid, ui_enable }) =>
      request(POST, "/assistant/update_default_assistant", {
        payload: { assistantid, ui_enable },
      }),
  });
}

export function useUpdateAccountAssistant() {
  return useMutation({
    mutationFn: ({
      assistantid,
      profile_id,
      assitentdefault_value_change,
      assistant_type,
    }) =>
      request(POST, "/assistant/updateaccount", {
        payload: {
          assistantid,
          profile_id,
          assitentdefault_value_change,
          assistant_type,
        },
      }),
  });
}

// YourAIAgents/AgentCard's "Use Agent" click — required-integrations lookup
// for a single assistant. Fetched on demand (not on mount), so modeled as a
// mutation like webhooks.js's useWebhookConfigSecret.
export function useAssistantIntegrations() {
  return useMutation({
    mutationFn: (assistantId) =>
      request(GET, "/assistant/integrations", {
        query: `assistant_id=${assistantId}`,
      }),
  });
}

// POST /prompts/account/generate — the "Create Agent" modals' one-shot
// AI prompt-fill step. Shared by YourAIAgents/components/CreateModal and
// Settings/components/AgentManager/CreateModal: same payload shape
// (account_id, username, promptQuery, type, file_ids) and same response
// shape at both call sites.
export function useGenerateAgentPrompt() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/prompts/account/generate", { payload }),
  });
}

// PUT /agency — agency.update in the old registry (SubCategory tag CRUD).
export function useUpdateAgency() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/agency", { payload }),
  });
}

// PUT /assistant/pause-agents — AgentCard's pause/activate action for a
// shared (non-account-owned) agent. assistant.pauseShareAgents in the old
// registry.
export function usePauseShareAgents() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/assistant/pause-agents", { payload }),
  });
}
